import React, { useEffect, useId } from 'react';

/* ============================================================================
   Zana — Trasse's official mascot: a friendly, purple-eyed pocket robot.

   Faithful reproduction of zana/Zana Animated.dc.html (the "alive" kit, viewBox
   0 0 240 230) plus the two canonical states the animated file omits — Eureka
   (star eyes, savings found) and Sleeping (Basic/pilot upsell). Self-contained:
   inlines the SVG + injects all keyframes once, and namespaces its gradient ids
   per instance (useId) so any number of Zanas can share a page. No external deps.

   Renders ONLY for Premium/Enterprise surfaces (callers gate on the subscription);
   Basic/pilot see Zana only as the sleeping upsell.

   <Zana state="online" size={120} animated ground />
   ========================================================================== */

export type ZanaState =
  | 'online'    // resting / idle on a loaded card
  | 'scanning'  // a briefing / insight is generating ("reading your fleet…")
  | 'happy'     // healthy fleet / good news
  | 'alert'     // a fuel-theft / urgent flag surfaced
  | 'thinking'  // the 7-day indexing window ("learning your fleet")
  | 'eureka'    // savings / opportunity found
  | 'wink'      // positive nudge
  | 'talking'   // delivering a coaching line / notification
  | 'sleeping'  // Basic/pilot upsell, or "taking a break" (cap hit / idle)
  | 'loading'   // inline working pill
  | 'concerned' // a route has been rough lately
  | 'offline'   // no connection — can't reach the fleet
  | 'reconnecting';// retrying the connection

export interface ZanaProps {
  state?: ZanaState;
  /** Rendered box in px (square). Hero ~300, card ~120, band ~28, tab icon ~18. */
  size?: number;
  animated?: boolean;
  /** Show the floating shadow + pulsing purple ground glow (larger placements). */
  ground?: boolean;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}

/* -- all keyframes, injected once (values verbatim from the mascot mocks) ---- */
const KEYFRAMES = `
@keyframes zFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-13px)}}
@keyframes zGlow{0%,100%{opacity:.28;transform:scale(.78)}50%{opacity:.9;transform:scale(1.12)}}
@keyframes zBlink{0%,91%,100%{transform:scaleY(1)}95%{transform:scaleY(.08)}}
@keyframes zPulse{0%,100%{opacity:.35;transform:scale(.7)}50%{opacity:1;transform:scale(1.15)}}
@keyframes zAnt{0%,100%{transform:rotate(-5deg)}50%{transform:rotate(5deg)}}
@keyframes zAntB{0%,100%{transform:rotate(5deg)}50%{transform:rotate(-5deg)}}
@keyframes zEyeGlow{0%,100%{opacity:.85}50%{opacity:1}}
@keyframes zTalk{0%,100%{transform:scaleY(.35)}50%{transform:scaleY(1)}}
@keyframes zScan{0%,100%{transform:translateY(-26px)}50%{transform:translateY(28px)}}
@keyframes zBang{0%{transform:scale(0) rotate(-24deg);opacity:0}16%{transform:scale(1.25) rotate(8deg);opacity:1}30%,84%{transform:scale(1) rotate(0);opacity:1}92%{transform:scale(1);opacity:.55}100%{transform:scale(1);opacity:1}}
@keyframes zThink{0%{transform:translateY(6px) scale(.4);opacity:0}30%{opacity:1}100%{transform:translateY(-18px) scale(1);opacity:0}}
@keyframes zSpark{0%,100%{opacity:0;transform:scale(.4)}50%{opacity:1;transform:scale(1)}}
@keyframes zNoSig{0%,100%{opacity:1}50%{opacity:.28}}
@keyframes zDrift{0%,100%{transform:translateY(0) rotate(-2deg)}50%{transform:translateY(-6px) rotate(2deg)}}
@keyframes zSpin{to{transform:rotate(360deg)}}
.zana-root svg *{transform-box:view-box;}
.zana-static *{animation:none!important;}
`;

let injected = false;
function ensureKeyframes() {
  if (injected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.setAttribute('data-zana-keyframes', '');
  el.textContent = KEYFRAMES;
  document.head.appendChild(el);
  injected = true;
}

/* -- 5-point star path (Eureka eyes) ---------------------------------------- */
function star5(cx: number, cy: number, R: number): string {
  const inner = R * 0.42;
  let d = '';
  for (let i = 0; i < 10; i++) {
    const rr = i % 2 === 0 ? R : inner;
    const ang = -Math.PI / 2 + (i * Math.PI) / 5;
    const x = (cx + rr * Math.cos(ang)).toFixed(1);
    const y = (cy + rr * Math.sin(ang)).toFixed(1);
    d += (i === 0 ? 'M' : 'L') + x + ' ' + y + ' ';
  }
  return d + 'Z';
}

/* -- face builders (240x230 geometry: eyes (98,116)/(142,116), mouth ~y142) -- */
function eye(cx: number, cy: number, r: number, gid: string, a: boolean, dur = '5.2s'): string {
  const hl = (r * 0.26).toFixed(1);
  const anim = a ? `animation:zBlink ${dur} infinite;` : '';
  return `<g style="transform-origin:${cx}px ${cy}px;${anim}"><circle cx="${cx}" cy="${cy}" r="${r}" fill="url(#zEye-${gid})"/><circle cx="${cx - 5}" cy="${cy - 5}" r="${hl}" fill="#efe8ff"/></g>`;
}
function orbs(r: number, gid: string, a: boolean): string {
  return `<g class="z-eyes">${eye(98, 116, r, gid, a)}${eye(142, 116, r, gid, a)}</g>`;
}
const CH =
  '<circle cx="82" cy="140" r="6" fill="rgba(155,124,255,.28)"/><circle cx="158" cy="140" r="6" fill="rgba(155,124,255,.28)"/>';

function faceInner(state: ZanaState, gid: string, a: boolean): string {
  const E = `url(#zEye-${gid})`;
  switch (state) {
    case 'happy':
      return `<path d="M86 120 Q98 106 110 120" fill="none" stroke="${E}" stroke-width="6" stroke-linecap="round"/>
        <path d="M130 120 Q142 106 154 120" fill="none" stroke="${E}" stroke-width="6" stroke-linecap="round"/>
        <path d="M100 142 Q120 158 140 142" fill="none" stroke="#9b7cff" stroke-width="4" stroke-linecap="round"/>
        <circle cx="82" cy="142" r="8" fill="rgba(155,124,255,.4)"/><circle cx="158" cy="142" r="8" fill="rgba(155,124,255,.4)"/>
        <g style="transform-origin:70px 92px;${a ? 'animation:zSpark 2.2s ease-in-out infinite;' : ''}"><path d="M70 84 l2.4 6.4 l6.4 2.4 l-6.4 2.4 l-2.4 6.4 l-2.4 -6.4 l-6.4 -2.4 l6.4 -2.4 Z" fill="#c9b6ff"/></g>
        <g style="transform-origin:172px 96px;${a ? 'animation:zSpark 2.2s .7s ease-in-out infinite;' : ''}"><path d="M172 88 l2 5.4 l5.4 2 l-5.4 2 l-2 5.4 l-2 -5.4 l-5.4 -2 l5.4 -2 Z" fill="#c9b6ff"/></g>`;
    case 'talking':
      return `${orbs(14, gid, a)}<ellipse cx="120" cy="144" rx="9" ry="7" fill="#9b7cff" style="transform-origin:120px 144px;${a ? 'animation:zTalk .42s ease-in-out infinite;' : ''}"/>${CH}`;
    case 'thinking':
      return `${orbs(13, gid, a)}<circle cx="120" cy="146" r="3.6" fill="#9b7cff"/>
        <circle cx="176" cy="66" r="3" fill="#9b7cff" style="transform-origin:176px 66px;${a ? 'animation:zThink 2.4s ease-in-out infinite;' : ''}"/>
        <circle cx="188" cy="54" r="4" fill="#9b7cff" style="transform-origin:188px 54px;${a ? 'animation:zThink 2.4s .5s ease-in-out infinite;' : ''}"/>
        <circle cx="202" cy="40" r="5" fill="#9b7cff" style="transform-origin:202px 40px;${a ? 'animation:zThink 2.4s 1s ease-in-out infinite;' : ''}"/>`;
    case 'scanning':
      return `<rect x="84" y="112" width="28" height="8" rx="4" fill="${E}"/><rect x="128" y="112" width="28" height="8" rx="4" fill="${E}"/>
        <g style="${a ? 'animation:zScan 1.7s ease-in-out infinite;' : ''}"><line x1="76" y1="120" x2="164" y2="120" stroke="#9b7cff" stroke-width="3" stroke-linecap="round" opacity=".65"/></g>
        <line x1="108" y1="147" x2="132" y2="147" stroke="#9b7cff" stroke-width="3" stroke-linecap="round"/>`;
    case 'loading':
      return `${orbs(14, gid, a)}<circle cx="106" cy="148" r="3.4" fill="#9b7cff" style="transform-origin:106px 148px;${a ? 'animation:zPulse 1.1s 0s infinite;' : ''}"/><circle cx="120" cy="148" r="3.4" fill="#9b7cff" style="transform-origin:120px 148px;${a ? 'animation:zPulse 1.1s .2s infinite;' : ''}"/><circle cx="134" cy="148" r="3.4" fill="#9b7cff" style="transform-origin:134px 148px;${a ? 'animation:zPulse 1.1s .4s infinite;' : ''}"/>`;
    case 'alert':
      return `<g class="z-eyes"><g style="transform-origin:98px 116px;${a ? 'animation:zBlink 4s infinite;' : ''}"><circle cx="98" cy="116" r="15" fill="${E}"/><circle cx="98" cy="116" r="4.2" fill="#fff"/></g><g style="transform-origin:142px 116px;${a ? 'animation:zBlink 4s infinite;' : ''}"><circle cx="142" cy="116" r="15" fill="${E}"/><circle cx="142" cy="116" r="4.2" fill="#fff"/></g></g>
        <circle cx="120" cy="147" r="4" fill="#9b7cff"/>
        <g style="transform-origin:198px 58px;${a ? 'animation:zBang 1.6s ease-out infinite;' : ''}"><circle cx="198" cy="58" r="15" fill="#ff9d5b"/><rect x="195.5" y="49" width="5" height="12" rx="2.5" fill="#fff"/><circle cx="198" cy="65" r="2.6" fill="#fff"/></g>`;
    case 'concerned':
      return `<line x1="84" y1="103" x2="108" y2="110" stroke="#9b7cff" stroke-width="4" stroke-linecap="round"/><line x1="156" y1="103" x2="132" y2="110" stroke="#9b7cff" stroke-width="4" stroke-linecap="round"/>
        <g class="z-eyes">${eye(98, 122, 11, gid, a)}${eye(142, 122, 11, gid, a)}</g>
        <path d="M104 152 Q120 142 136 152" fill="none" stroke="#9b7cff" stroke-width="4" stroke-linecap="round"/>`;
    case 'wink':
      return `<path d="M86 118 Q98 128 110 118" fill="none" stroke="${E}" stroke-width="6" stroke-linecap="round"/>
        ${eye(142, 116, 14, gid, a)}
        <path d="M100 142 Q120 154 140 142" fill="none" stroke="#9b7cff" stroke-width="4" stroke-linecap="round"/>${CH}
        <g style="transform-origin:74px 100px;${a ? 'animation:zSpark 1.8s ease-in-out infinite;' : ''}"><path d="M74 94 l1.8 4.8 l4.8 1.8 l-4.8 1.8 l-1.8 4.8 l-1.8 -4.8 l-4.8 -1.8 l4.8 -1.8 Z" fill="#c9b6ff"/></g>`;
    case 'eureka':
      return `<path d="${star5(98, 116, 15)}" fill="${E}"/><path d="${star5(142, 116, 15)}" fill="${E}"/>
        <path d="M100 142 Q120 154 140 142" fill="none" stroke="#9b7cff" stroke-width="4" stroke-linecap="round"/>${CH}
        <g style="transform-origin:70px 92px;${a ? 'animation:zSpark 2s ease-in-out infinite;' : ''}"><path d="M70 84 l2.4 6.4 l6.4 2.4 l-6.4 2.4 l-2.4 6.4 l-2.4 -6.4 l-6.4 -2.4 l6.4 -2.4 Z" fill="#c9b6ff"/></g>
        <g style="transform-origin:174px 96px;${a ? 'animation:zSpark 2s .6s ease-in-out infinite;' : ''}"><path d="M174 88 l2 5.4 l5.4 2 l-5.4 2 l-2 5.4 l-2 -5.4 l-5.4 -2 l5.4 -2 Z" fill="#c9b6ff"/></g>`;
    case 'sleeping':
      return `<path d="M86 116 Q98 124 110 116" fill="none" stroke="${E}" stroke-width="5" stroke-linecap="round"/>
        <path d="M130 116 Q142 124 154 116" fill="none" stroke="${E}" stroke-width="5" stroke-linecap="round"/>
        <path d="M108 146 Q120 152 132 146" fill="none" stroke="#9b7cff" stroke-width="3" stroke-linecap="round"/>
        <text x="158" y="72" font-size="20" font-weight="800" fill="#b9a9e6" font-family="'Bricolage Grotesque',sans-serif" style="${a ? 'animation:zSpark 3s ease-in-out infinite;' : ''}">z</text>
        <text x="174" y="54" font-size="26" font-weight="800" fill="#b9a9e6" font-family="'Bricolage Grotesque',sans-serif" style="${a ? 'animation:zSpark 3s .5s ease-in-out infinite;' : ''}">Z</text>`;
    case 'offline':
      // X-eyes + flat mouth + a struck-through signal-bars icon (no connection).
      return `<g stroke="#8a92ab" stroke-width="5" stroke-linecap="round"><line x1="90" y1="110" x2="104" y2="123"/><line x1="104" y1="110" x2="90" y2="123"/><line x1="136" y1="110" x2="150" y2="123"/><line x1="150" y1="110" x2="136" y2="123"/></g>
        <path d="M104 152 Q112 146 120 152 Q128 158 136 152" fill="none" stroke="#8a92ab" stroke-width="4" stroke-linecap="round"/>
        <g style="transform-origin:198px 56px;${a ? 'animation:zNoSig 1.8s ease-in-out infinite;' : ''}"><rect x="185" y="60" width="5" height="6" rx="1.5" fill="#8a92ab"/><rect x="193" y="55" width="5" height="11" rx="1.5" fill="#8a92ab"/><rect x="201" y="49" width="5" height="17" rx="1.5" fill="#5a627a"/><line x1="181" y1="66" x2="211" y2="46" stroke="#ff6b6b" stroke-width="4.5" stroke-linecap="round"/></g>`;
    case 'reconnecting':
      return `${orbs(13, gid, a)}<circle cx="120" cy="148" r="3.4" fill="#9b7cff"/>
        <g style="transform-origin:198px 57px;${a ? 'animation:zSpin 1s linear infinite;' : ''}"><path d="M198 45 a12 12 0 1 1 -11.4 8.4" fill="none" stroke="#9b7cff" stroke-width="4" stroke-linecap="round"/><path d="M186 55 l1.2 -7 l6.4 3.2 Z" fill="#9b7cff"/></g>`;
    default: // online / idle
      return `${orbs(14, gid, a)}<path d="M104 142 Q120 152 136 142" fill="none" stroke="#9b7cff" stroke-width="4" stroke-linecap="round"/>${CH}`;
  }
}

export default function Zana({
  state = 'online',
  size = 120,
  animated = true,
  ground = false,
  title = 'Zana',
  className = '',
  style,
}: ZanaProps) {
  const rid = useId().replace(/[:]/g, '');
  const gid = `z${rid}`;
  useEffect(() => { ensureKeyframes(); }, []);

  const a = animated;
  const face = faceInner(state, gid, a);
  const floatDur = state === 'sleeping' ? '6.2s' : state === 'offline' ? '5.6s' : '4.4s';

  return (
    <span
      className={`zana-root${a ? '' : ' zana-static'}${className ? ` ${className}` : ''}`}
      role="img"
      aria-label={title}
      style={{ display: 'inline-block', width: size, height: size, lineHeight: 0, ...style }}
    >
      <span
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          transformOrigin: '50% 62%',
          animation: a ? `zFloat ${floatDur} ease-in-out infinite` : undefined,
        }}
      >
        <svg viewBox="0 0 240 230" width="100%" height="100%" style={{ display: 'block', overflow: 'visible' }}>
          <defs>
            <linearGradient id={`zMetal-${gid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#f6f8fc" />
              <stop offset="1" stopColor="#cdd5e6" />
            </linearGradient>
            <radialGradient id={`zEye-${gid}`} cx="38%" cy="32%" r="72%">
              <stop offset="0" stopColor="#c9b6ff" />
              <stop offset="0.58" stopColor="#8a63f0" />
              <stop offset="1" stopColor="#6f4ff0" />
            </radialGradient>
            <linearGradient id={`zPurple-${gid}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#b49bff" />
              <stop offset="1" stopColor="#6f4ff0" />
            </linearGradient>
          </defs>

          {ground && (
            <>
              <ellipse cx="120" cy="212" rx="60" ry="12" fill="rgba(60,40,90,.13)" />
              <g style={{ transformOrigin: '120px 196px', animation: a ? 'zGlow 2.6s ease-in-out infinite' : undefined }}>
                <ellipse cx="120" cy="196" rx="52" ry="13" fill="rgba(111,79,240,.35)" />
              </g>
            </>
          )}

          {/* antennae (counter-phased sway) */}
          <g style={{ transformOrigin: '88px 56px', animation: a ? 'zAnt 3s ease-in-out infinite' : undefined }}>
            <line x1="88" y1="56" x2="78" y2="28" stroke="#9aa6c2" strokeWidth="5" strokeLinecap="round" />
            <circle cx="78" cy="23" r="7.5" fill="#6f4ff0" />
            <circle cx="75" cy="20" r="2.4" fill="#c9b6ff" />
          </g>
          <g style={{ transformOrigin: '152px 56px', animation: a ? 'zAntB 3s ease-in-out infinite' : undefined }}>
            <line x1="152" y1="56" x2="162" y2="28" stroke="#9aa6c2" strokeWidth="5" strokeLinecap="round" />
            <circle cx="162" cy="23" r="7.5" fill="#9b7cff" />
            <circle cx="159" cy="20" r="2.4" fill="#d9ccff" />
          </g>

          {/* thrusters */}
          <rect x="40" y="94" width="22" height="50" rx="11" fill="#c2cadd" />
          <rect x="178" y="94" width="22" height="50" rx="11" fill="#c2cadd" />

          {/* head + gloss + top nub */}
          <rect x="52" y="54" width="136" height="130" rx="42" fill={`url(#zMetal-${gid})`} stroke="#b9c2d6" strokeWidth="2" />
          <ellipse cx="86" cy="82" rx="22" ry="12" fill="rgba(255,255,255,.6)" transform="rotate(-18 86 82)" />
          <rect x="108" y="48" width="24" height="9" rx="4.5" fill="#9aa6c2" />

          {/* screen + inner tint */}
          <rect x="70" y="76" width="100" height="86" rx="30" fill="#20223a" />
          <rect x="80" y="86" width="80" height="24" rx="12" fill="rgba(155,124,255,.1)" />

          {/* swappable face (eyes + mouth per state) with a gentle eye-glow */}
          <g
            style={{ transformOrigin: '120px 118px', animation: a ? 'zEyeGlow 2.4s ease-in-out infinite' : undefined }}
            dangerouslySetInnerHTML={{ __html: face }}
          />
        </svg>
      </span>
    </span>
  );
}
